import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhyChooseSection } from '../components/WhyChooseSection';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { CTASection } from '../components/CTASection';
import { Shield, Heart, Award, Users, Clock, Star, ArrowRight, Check } from 'lucide-react';

const values = [
  {
    icon: Heart,
    title: 'Compassion First',
    description: 'Every interaction is guided by genuine empathy and understanding. We treat your loved ones as we would our own family.',
  },
  {
    icon: Shield,
    title: 'Unwavering Trust',
    description: 'Rigorous background checks, ongoing training, and consistent quality assurance ensure dependable care you can count on.',
  },
  {
    icon: Users,
    title: 'Personalized Approach',
    description: 'No two care plans are alike. We tailor our services to match individual needs, preferences, and lifestyles.',
  },
  {
    icon: Star,
    title: 'Excellence in Care',
    description: 'Our caregivers are carefully selected and continuously trained to deliver the highest standards of in-home care.',
  },
  {
    icon: Clock,
    title: 'Reliable Support',
    description: 'Consistent, punctual, and dependable service—because families deserve peace of mind, not uncertainty.',
  },
  {
    icon: Award,
    title: 'Dignity & Respect',
    description: 'We honor the independence and preferences of every individual, promoting autonomy while providing support.',
  },
];

const differentiators = [
  'Family-owned and operated with deep community roots',
  'Personalized matching between caregivers and clients',
  'Transparent communication and regular family updates',
  'Flexible scheduling with no long-term contracts required',
  '24/7 on-call support for emergencies',
  'Continuous quality monitoring and feedback',
];

export const WhyHeritagePage = () => {
  return (
    <>
      <Helmet>
        <title>Why Choose Heritage Home Health LLC | Trusted In-Home Care</title>
        <meta
          name="description"
          content="Discover why families trust Heritage Home Health LLC for in-home care. 15+ years of compassionate service, personalized care plans, and dedicated caregivers."
        />
      </Helmet>
      <div className="min-h-screen bg-warm-beige" data-testid="why-heritage-page">
        <Navigation />
        <main>
          {/* Hero Section */}
          <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-deep-blue/5 via-transparent to-transparent" />
            <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-soft-gold/15 to-transparent rounded-full blur-3xl" />
            
            <div className="relative max-w-7xl mx-auto px-6 md:px-12">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                    Why Choose Us
                  </span>
                  <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy mb-6">
                    Why Families Choose{' '}
                    <span className="text-gradient-gold">Heritage Home Health LLC</span>
                  </h1>
                  <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
                    For over 15 years, Heritage Home Health LLC has been the trusted choice 
                    for families seeking compassionate, reliable in-home care. Our commitment 
                    to excellence sets us apart.
                  </p>
                  <Link
                    to="/contact"
                    data-testid="why-heritage-hero-cta"
                    className="group inline-flex items-center space-x-2 bg-navy text-white px-8 py-4 rounded-full font-semibold hover:bg-deep-blue transition-all shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1"
                  >
                    <span>Start Your Care Journey</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative"
                >
                  <div className="glass-strong shadow-soft-3d-lg rounded-3xl p-8">
                    <h3 className="font-serif text-2xl text-navy mb-6">The Heritage Difference</h3>
                    <ul className="space-y-4">
                      {differentiators.map((item, index) => (
                        <motion.li
                          key={index}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + index * 0.1 }}
                          className="flex items-start space-x-3"
                        >
                          <div className="w-6 h-6 rounded-full bg-soft-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-soft-gold" />
                          </div>
                          <span className="text-gray-700">{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                  <div className="absolute -bottom-6 -right-6 -z-10 w-full h-full rounded-3xl bg-gradient-to-br from-soft-gold/20 to-muted-green/10" />
                </motion.div>
              </div>
            </div>
          </section>

          {/* Values Section */}
          <section className="py-16 md:py-24 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
                  Our Values
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-navy mb-6">
                  The Principles That <span className="text-gradient-gold">Guide Our Care</span>
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Every decision we make at Heritage Home Health LLC is guided by these core values, 
                  ensuring that every family receives the exceptional care they deserve.
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {values.map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <motion.div
                      key={value.title}
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="group p-8 rounded-3xl bg-warm-beige hover:bg-white hover:shadow-soft-3d-lg transition-all duration-500 hover:-translate-y-2"
                      data-testid={`value-card-${index}`}
                    >
                      <div className="w-14 h-14 rounded-2xl bg-soft-gold/20 flex items-center justify-center mb-6 group-hover:bg-soft-gold/30 transition-colors">
                        <Icon className="w-7 h-7 text-soft-gold" />
                      </div>
                      <h3 className="font-serif text-2xl text-navy mb-3 group-hover:text-soft-gold transition-colors">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>

          <WhyChooseSection />
          <TestimonialsSection />
          <CTASection />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default WhyHeritagePage;
